import {
  useCountryId,
  useStoreId,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";
import { fetchItemsInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemsInfo";
import { Item } from "@coworker/apprestructured/src/shared/types/item";
import { LocalCombinationItem } from "@coworker/apprestructured/src/shared/types/localCombinationItem";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { FixaFormField } from "@coworker/apprestructured/src/shared/wrappers/FixaFormField/FixaFormField";
import { FixaList } from "@coworker/apprestructured/src/shared/wrappers/FixaList/FixaList";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import FixaTabs, {
  FixaTab,
  FixaTabPanel,
} from "@coworker/apprestructured/src/shared/wrappers/FixaTabs/FixaTabs";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { FixaTextArea } from "@coworker/apprestructured/src/shared/wrappers/FixaTextArea/FixaTextArea";
import {
  ArticleWithQuantity,
  AttachArticlesWithQuantityStepper,
} from "@coworker/apprestructured/src/task-management/components/AttachArticles/AttachArticlesWithQuantityStepper";
import { useTranslation } from "@coworker/locales";
import {
  detectBarcodeFormat,
  extractBasedOnSymbology,
} from "@ingka-group-digital/flexa-scanner";
import { useQueryClient } from "@tanstack/react-query";
import { ChangeEvent, useCallback, useMemo, useState } from "react";
import {
  getDexfCombination,
  mapDexfCombinationToLocalCombinationItem,
} from "../../components/RoomSettings/services/dexf.service";
import { fetchItemsInfoFromDataService } from "../../components/RoomSettings/services/localCombination.service";
import { logEvent } from "../../helpers/tracker";
import { addArticlesBulk } from "../RoomSettings/services/articles.service";
import { Article } from "../RoomSettings/types/article";
import {
  extractProductArticleIdAndQuantity,
  isValidInput,
  MAX_QUANTITY_NUMBER,
  ParseStatus,
  removeDuplicateArticles,
  transformArticlesInfos,
} from "./AddMultipleArticles.helper";
import styles from "./AddMultipleArticles.module.css";
import { useInputPopup } from "./index";
import { useArticlesByMediaId } from "../RoomSettings/hooks/useArticlesByMediaId";
import { useArticlesByGroupId } from "../RoomSettings/hooks/useArticlesByGroupId";

const MAX_ARTICLES = 150;

interface AddMultipleArticlesType {
  isOpen: boolean;
  mediaId: string;
  groupId: string;
  countryId: string;
  setIsAddingArticles: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AddMultipleArticles = ({
  mediaId,
  groupId,
  setIsAddingArticles = () => {},
}: AddMultipleArticlesType) => {
  const { t } = useTranslation();
  const { closePopup } = useInputPopup();
  const storeId = useStoreId();
  const userCountryId = useCountryId();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState("attach");

  const [scannedArticles, setScannedArticles] = useState<ArticleWithQuantity[]>(
    []
  );
  const [inputValue, setInputValue] = useState("");
  const enableDefx = useFlag(FLAGS.ENABLE_DEXF);
  const { data: mediaArticles, isLoading: isMediaArticlesLoading } =
    useArticlesByMediaId(groupId ? undefined : mediaId, "ACTIVE");
  const { data: groupArticles, isLoading: isGroupArticlesLoading } =
    useArticlesByGroupId(groupId);
  const isExistingArticlesLoading = groupId
    ? isGroupArticlesLoading
    : isMediaArticlesLoading;

  const preselectedItemNos = useMemo(() => {
    const articles = groupId ? (groupArticles ?? []) : (mediaArticles ?? []);
    return articles.map((article) => article.productArticleId);
  }, [groupId, groupArticles, mediaArticles]);

  const existingArticleNos = useMemo(
    () => new Set(preselectedItemNos),
    [preselectedItemNos]
  );

  const hasNewScannedArticles = useMemo(
    () => scannedArticles.some(({ item }) => !existingArticleNos.has(item.no)),
    [existingArticleNos, scannedArticles]
  );

  const parseStatus = isValidInput(inputValue, {
    validateArticleNumber: !enableDefx,
  });

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;

      const existingArticles = inputValue.match(/\d{8}/g) || [];
      const newArticles = newValue.match(/\d{8}/g) || [];
      const addedCount = newArticles.length - existingArticles.length;
      if (addedCount >= 2) {
        logEvent("ce:add_multiple_articles_by_multiline_paste", {
          addedCount,
          totalCount: newArticles.length,
          isMobile: false,
        });
      }

      const lines = newValue.split("\n");
      const lastLineIndex = lines.length - 1;
      const cleanedLines = lines.map((line, index) => {
        if (index === lastLineIndex) return line;
        const trimmed = line.trim();
        const format = detectBarcodeFormat(trimmed);
        if (
          format === "INCOMPLETE" ||
          format === "UNKNOWN" ||
          format === "ARTICLE_NUMBER"
        ) {
          return line;
        }
        try {
          return extractBasedOnSymbology(format, trimmed).articleNumber;
        } catch {
          return line;
        }
      });
      setInputValue(cleanedLines.join("\n"));
    },
    [inputValue]
  );

  const parsedArticles = useMemo(() => {
    if (!inputValue || parseStatus !== ParseStatus.ParseOk) {
      return [];
    }
    return extractProductArticleIdAndQuantity(inputValue);
  }, [inputValue, parseStatus]);

  const uniqueInsertArticleCount = useMemo(
    () => new Set(parsedArticles.map((a) => a.productArticleId)).size,
    [parsedArticles]
  );

  const totalInsertArticleCount = useMemo(
    () => parsedArticles.reduce((sum, a) => sum + a.quantity, 0),
    [parsedArticles]
  );

  const handleAttachSubmit = useCallback(async () => {
    setIsAddingArticles(true);
    closePopup();

    const articles: Partial<Article>[] = scannedArticles
      .filter(({ item }) => !existingArticleNos.has(item.no))
      .map(({ item, quantity }) => ({
        active: "ACTIVE" as const,
        hfb: item.hfbNo || "Unknown",
        nbrArticles: quantity,
        itemName: item.name || "Unknown",
        itemType: item.description ?? "Unknown",
        itemColor: item.description?.split(",")[1] || "Unknown",
        imageSmall: item.smallImageUrl || item.imageUrl || "Unknown",
        productArticleType: item.type || "ART",
        productArticleId: item.no,
        mediaId: mediaId || "",
        articlesCountInCombination: 0,
        parentArticleCombinationNumber: "",
      }));

    if (articles.length > 0) {
      try {
        await addArticlesBulk(articles, groupId, queryClient);
        logEvent("ce:add_multiple_articles_submit", {
          addedCount: articles.length,
          isMobile: false,
        });
      } catch {
        alert(t("searchErrorString"));
      }
    }

    setIsAddingArticles(false);
  }, [
    scannedArticles,
    mediaId,
    groupId,
    existingArticleNos,
    queryClient,
    closePopup,
    setIsAddingArticles,
    t,
  ]);

  const handleInsertSubmit = useCallback(async () => {
    setIsAddingArticles(true);
    closePopup();

    const formattedInputValues = extractProductArticleIdAndQuantity(inputValue);
    const productArticleIds = formattedInputValues
      .map((o) => o.productArticleId)
      .join(",");
    const itemsInfo: Item[] = await queryClient.ensureQueryData({
      queryKey: ["items", "info", productArticleIds],
      queryFn: () => fetchItemsInfo(productArticleIds),
    });

    const allItemsInfo = [...itemsInfo];

    const articlesWithoutData = formattedInputValues.filter(
      (article) =>
        !itemsInfo.some((item) => item.no === article.productArticleId)
    );

    if (articlesWithoutData.length > 0) {
      const missingNos = articlesWithoutData
        .map((a) => a.productArticleId)
        .join(",");

      const fetchedCombinations =
        (await fetchItemsInfoFromDataService(
          storeId,
          userCountryId,
          missingNos
        )) || [];

      let fetchedDexfCombinations: LocalCombinationItem[] = [];
      if (enableDefx) {
        const articlesWithoutLocalCombinationData = articlesWithoutData.filter(
          (article) =>
            !fetchedCombinations.some(
              (combination) => combination.no === article.productArticleId
            )
        );

        if (articlesWithoutLocalCombinationData.length > 0) {
          const dexfCombinations = await Promise.all(
            articlesWithoutLocalCombinationData.map((article) =>
              getDexfCombination(article.productArticleId)
            )
          );

          fetchedDexfCombinations = dexfCombinations
            .flatMap((combination) => combination ?? [])
            .map((combination) =>
              mapDexfCombinationToLocalCombinationItem(
                combination,
                userCountryId
              )
            );
        }
      }

      const fetchedCombinationItems: LocalCombinationItem[] = [
        ...fetchedCombinations,
        ...fetchedDexfCombinations,
      ];

      const combinationEntries = fetchedCombinationItems.map((combo) => ({
        parentArticle: combo.no,
        parentArticleQuantity: formattedInputValues.find(
          (item) => item.productArticleId === combo.no
        )?.quantity,
        itemNos: combo?.itemsInCombination?.map((i) => i.item_no) ?? [],
      }));

      const allCombinationItemNos = combinationEntries.flatMap(
        ({ parentArticle, parentArticleQuantity, itemNos }) => {
          itemNos.forEach((no, index) => {
            const quantity =
              fetchedCombinationItems.find(
                (combo) => combo.no === parentArticle
              )?.itemsInCombination?.[index]?.quantity || 1;

            formattedInputValues.push({
              productArticleId: no,
              quantity: parentArticleQuantity ?? 1,
              parentArticle,
              articlesCountInCombination: quantity,
            });
          });
          return itemNos;
        }
      );

      const articlesInCombinationInfo: Item[] = allCombinationItemNos.length
        ? await queryClient.ensureQueryData({
            queryKey: ["items", "info", allCombinationItemNos],
            queryFn: () => fetchItemsInfo(allCombinationItemNos.join(",")),
          })
        : [];

      allItemsInfo.push(
        ...articlesInCombinationInfo,
        ...fetchedCombinationItems
      );
    }

    const allArticles = transformArticlesInfos(
      mediaId,
      formattedInputValues,
      allItemsInfo
    );

    const uniqueArticles = removeDuplicateArticles(allArticles);
    if (allArticles.length > 0) {
      try {
        await addArticlesBulk(uniqueArticles, groupId, queryClient);
        logEvent("ce:add_multiple_articles_submit", {
          addedCount: uniqueArticles.length,
          isMobile: false,
        });
      } catch {
        alert(t("searchErrorString"));
      }
    }

    setIsAddingArticles(false);
  }, [
    inputValue,
    mediaId,
    groupId,
    storeId,
    userCountryId,
    queryClient,
    closePopup,
    setIsAddingArticles,
    enableDefx,
    t,
  ]);

  const handleSubmit = useCallback(async () => {
    if (activeTab === "attach") {
      await handleAttachSubmit();
    } else {
      await handleInsertSubmit();
    }
  }, [activeTab, handleAttachSubmit, handleInsertSubmit]);

  const isSubmitDisabled = useMemo(() => {
    if (activeTab === "attach") {
      return isExistingArticlesLoading || !hasNewScannedArticles;
    }
    return !inputValue || parseStatus !== ParseStatus.ParseOk;
  }, [
    activeTab,
    hasNewScannedArticles,
    inputValue,
    isExistingArticlesLoading,
    parseStatus,
  ]);

  return (
    <FixaModal
      visible
      handleCloseBtn={closePopup}
      focusLockProps={{ locked: false }}
    >
      <FixaSheets
        labelledById="add-multiple-articles-header"
        header={
          <FixaModalHeader
            id="add-multiple-articles-header"
            ariaCloseTxt={t("closeActionString")}
            title={t("addArticlesString")}
          />
        }
        footer={
          <FixaModalFooter>
            <FixaButton
              data-testid="addMultipleArticles:submit"
              type="primary"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
            >
              {t("addArticlesString")}
            </FixaButton>
          </FixaModalFooter>
        }
        fullSize
      >
        <FixaModalBody>
          <FixaTabs
            defaultActiveTab="attach"
            activeTab={activeTab}
            onTabChanged={(tabId: string) => setActiveTab(tabId)}
            tabs={[
              <FixaTab
                key="attach"
                text={t("addArticlesString")}
                tabPanelId="attach"
              />,
              <FixaTab
                key="insert"
                text={
                  enableDefx
                    ? t("insertArticlesAndDesignString")
                    : t("insertArticlesString")
                }
                tabPanelId="insert"
              />,
            ]}
            tabPanels={[
              <FixaTabPanel key="attach-panel" tabPanelId="attach">
                <AttachArticlesWithQuantityStepper
                  articles={scannedArticles}
                  setArticles={setScannedArticles}
                  maxArticles={MAX_ARTICLES}
                  isActive={activeTab === "attach"}
                  enableMultiSelectSearch
                  preselectedItemNos={preselectedItemNos}
                />
              </FixaTabPanel>,

              <FixaTabPanel key="insert-panel" tabPanelId="insert">
                {activeTab === "insert" && (
                  <>
                    {parsedArticles.length > 0 && (
                      <div className={styles["articleCountHeader"]}>
                        <FixaText textStyle="Heading.XS">
                          {uniqueInsertArticleCount === 1
                            ? `1 ${t("articleSelectedString")}`
                            : t("articlesSelectedCountString", {
                                count: uniqueInsertArticleCount,
                              })}{" "}
                          ({totalInsertArticleCount}{" "}
                          {t("totalString", { defaultValue: "total" })})
                        </FixaText>
                      </div>
                    )}
                    <FixaText textStyle="Label.S">
                      {t("scanArticleRDTString")}
                    </FixaText>
                    <FixaList size="small">
                      <li>{t("scanMultipleTimesToIncreaseQuantityString")}</li>
                      <li>{t("oneArticleNumberPerLineString")}</li>
                      <li>{t("addQuantityAfterArticleNumberString")}</li>
                      {enableDefx && <li>{t("addRexCodeString")}</li>}
                    </FixaList>
                    <FixaFormField
                      shouldValidate={
                        inputValue.length > 0 &&
                        parseStatus !== ParseStatus.ParseOk
                      }
                      validation={
                        parseStatus === ParseStatus.ParseErrorArticleNumber
                          ? {
                              msg: t("addArticleNumberString"),
                              id: "article-number",
                            }
                          : {
                              msg: t("addQuantityNumberString", {
                                maxQuantity: MAX_QUANTITY_NUMBER,
                              }),
                              id: "quantity-number",
                            }
                      }
                    >
                      <FixaTextArea
                        label=""
                        value={inputValue}
                        rows={12}
                        autoFocus
                        onChange={handleInputChange}
                        data-testid="addMultipleArticles:input"
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                      />
                    </FixaFormField>
                  </>
                )}
              </FixaTabPanel>,
            ]}
          />
        </FixaModalBody>
      </FixaSheets>
    </FixaModal>
  );
};
